import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import Modal from 'react-modal';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { getPeriodByDateService } from '../../../../../services/scheduling';
import { getDateFromString } from '../../../../../utility';
import { isResponseOk } from '../../../../../utility/isResponseOk';
import SelectWeekModal from '../SelectWeekModal';
import './styles.scss';

const PERIOD_DAYS_COUNT = 14;

const PrintEmployeeScheduleSelectModal = ({ employees, periodData, companyId, facilityId, closeModal, onConfirm }) => {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [dates, setDates] = useState(() =>
    Array.from({ length: PERIOD_DAYS_COUNT }, (_, i) => dayjs(periodData.start_date).add(i, 'day').toDate()),
  );
  const [isSelectDateModalOpen, setIsSelectDateModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const employeesOptions = useMemo(
    () => employees.map((employee) => ({ ...employee, value: employee.id, label: employee.full_name })),
    [employees],
  );

  // The shown period is printed as is; any other 2 weeks are loaded by their first day.
  const getPrintPeriodData = async () => {
    if (dayjs(dates[0]).isSame(periodData.start_date, 'day')) {
      return periodData;
    }

    const response = await getPeriodByDateService({
      company_id: companyId,
      facility_id: facilityId,
      date: dayjs(dates[0]).format('YYYY-MM-DD'),
    });
    // The backend falls back to the nearest later period when the chosen one does not exist.
    if (!isResponseOk(response) || !response.data || response.data.is_empty || !dayjs(response.data.start_date).isSame(dates[0], 'day')) {
      return null;
    }

    return {
      ...response.data,
      start_date: getDateFromString(response.data.start_date),
      end_date: getDateFromString(response.data.end_date),
    };
  };

  const handleConfirm = async () => {
    if (!selectedEmployee) {
      return;
    }

    setIsLoading(true);
    try {
      const printPeriodData = await getPrintPeriodData();
      if (!printPeriodData) {
        toast.error('There is no schedule for the selected period');
        return;
      }
      onConfirm(selectedEmployee, printPeriodData);
      closeModal();
    } catch (e) {
      console.error(e);
      toast.error('There is no schedule for the selected period');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal
        className="modal-alert print-employee-schedule-select-modal"
        isOpen
        onRequestClose={closeModal}
        contentLabel="Print Employee Schedule modal"
        ariaHideApp={false}
      >
        <h2 className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="5" y="5" width="38" height="38" rx="19" fill="#F4EBFF" />
              <rect x="5" y="5" width="38" height="38" rx="19" stroke="#F9F5FF" strokeWidth="10" />
              <g clipPath="url(#clip0_emp_print)">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M19.3334 17.3307C19.3334 16.9625 19.6319 16.6641 20.0001 16.6641H28.0001C28.3683 16.6641 28.6667 16.9625 28.6667 17.3307V21.3307H29.3334C29.8638 21.3307 30.3726 21.5414 30.7476 21.9165C31.1227 22.2916 31.3334 22.8003 31.3334 23.3307V26.6641C31.3334 27.1945 31.1227 27.7032 30.7476 28.0783C30.3726 28.4533 29.8638 28.6641 29.3334 28.6641H28.6667V30.6641C28.6667 31.0322 28.3683 31.3307 28.0001 31.3307H20.0001C19.6319 31.3307 19.3334 31.0322 19.3334 30.6641V28.6641H18.6667C18.1363 28.6641 17.6276 28.4533 17.2525 28.0783C16.8775 27.7032 16.6667 27.1945 16.6667 26.6641V23.3307C16.6667 22.8003 16.8775 22.2916 17.2525 21.9165C17.6276 21.5414 18.1363 21.3307 18.6667 21.3307H19.3334V17.3307ZM20.6667 29.9974H27.3334V25.9974H20.6667V29.9974ZM28.6667 27.3307V25.3307C28.6667 24.9625 28.3683 24.6641 28.0001 24.6641H20.0001C19.6319 24.6641 19.3334 24.9625 19.3334 25.3307V27.3307H18.6667C18.4899 27.3307 18.3204 27.2605 18.1953 27.1355C18.0703 27.0104 18.0001 26.8409 18.0001 26.6641V23.3307C18.0001 23.1539 18.0703 22.9843 18.1953 22.8593C18.3204 22.7343 18.4899 22.6641 18.6667 22.6641H29.3334C29.5102 22.6641 29.6798 22.7343 29.8048 22.8593C29.9298 22.9843 30.0001 23.1539 30.0001 23.3307V26.6641C30.0001 26.8409 29.9298 27.0104 29.8048 27.1355C29.6798 27.2605 29.5102 27.3307 29.3334 27.3307H28.6667ZM27.3334 17.9974V21.3307H20.6667V17.9974H27.3334Z"
                  fill="#7F56D9"
                />
              </g>
              <defs>
                <clipPath id="clip0_emp_print">
                  <rect width="16" height="16" fill="white" transform="translate(16 16)" />
                </clipPath>
              </defs>
            </svg>

            <span className="modal-header-title">Print Employee Schedule</span>
          </div>
          <button className="btn btn-icon" onClick={closeModal}>
            <i className="icon-icon-x"></i>
          </button>
        </h2>

        <div className="modal-inner-info">
          <div className="form-item">
            <label className="form-label">Choose employee</label>
            <Select
              isSearchable
              options={employeesOptions}
              className="form-control-select"
              classNamePrefix="react-select"
              value={selectedEmployee}
              placeholder="Select an employee"
              onChange={(e) => setSelectedEmployee(e)}
            />
          </div>
          <div className="schedule-date-wrapper">
            <span className="schedule-date-title">
              {dayjs(dates[0]).format('MMMM D, YYYY')} – {dayjs(dates[dates.length - 1]).format('MMMM D, YYYY')}
            </span>
            <button className="btn btn-outline select-week-btn" onClick={() => setIsSelectDateModalOpen(true)}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M13.3333 1.66406V4.9974M6.66667 1.66406V4.9974M2.5 8.33073H17.5M4.16667 3.33073H15.8333C16.7538 3.33073 17.5 4.07692 17.5 4.9974V16.6641C17.5 17.5845 16.7538 18.3307 15.8333 18.3307H4.16667C3.24619 18.3307 2.5 17.5845 2.5 16.6641V4.9974C2.5 4.07692 3.24619 3.33073 4.16667 3.33073Z"
                  stroke="#7F56D9"
                  strokeWidth="1.67"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={closeModal}>
            Cancel
          </button>
          <button
            className="btn"
            disabled={!selectedEmployee || isLoading}
            onClick={(e) => {
              e.stopPropagation();
              handleConfirm();
            }}
          >
            Print Schedule
          </button>
        </div>
      </Modal>

      {isSelectDateModalOpen && (
        <SelectWeekModal
          onClose={() => setIsSelectDateModalOpen(false)}
          periodData={periodData}
          setDates={setDates}
          dates={dates}
          daysCount={PERIOD_DAYS_COUNT}
        />
      )}
    </>
  );
};

export default PrintEmployeeScheduleSelectModal;
